import { useCallback, useEffect, useState } from 'react';

import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import {
  useExperiment,
  useFeature,
  useFeatures,
  useViewerData,
} from '@xing-com/hub';

type UsePayOrConsentStateResult = {
  shouldHoldConsentUi: boolean;
  shouldRenderPayOrConsentPlaceholder: boolean;
};

const CONSENT_CHANGE_EVENTS = new Set(['ACCEPT_ALL', 'DENY_ALL', 'SAVE']);
const USERCENTRICS_CONSENT_EVENT = 'UC_CONSENT';
const USERCENTRICS_UI_CMP_EVENT = 'UC_UI_CMP_EVENT';

async function areAllConsentsAccepted(): Promise<boolean | undefined> {
  if (typeof window === 'undefined' || !window.__ucCmp?.getConsentDetails) {
    return undefined;
  }

  try {
    const consentDetails = await window.__ucCmp.getConsentDetails();
    return consentDetails.consent.status === 'ALL_ACCEPTED';
  } catch {
    return undefined;
  }
}

export function usePayOrConsentState(): UsePayOrConsentStateResult {
  const { isLoggedIn } = useLoginState();
  const payOrConsentExperimentEnabled = useExperiment('ABACUS-546') === 'B';
  const { loading: loadingFeatures } = useFeatures();
  const isBasic = useFeature('isBasic', true);
  const activePayingMember =
    isLoggedIn && !loadingFeatures && isBasic === false;
  const { data: viewerData, loading: loadingViewerData } = useViewerData();
  const acceptedAllConsentsFromViewer =
    viewerData?.hasThirdPartyTrackingConsent ?? false;
  const [acceptedAllConsentsFromUsercentrics, setAcceptedAllConsents] =
    useState<boolean | undefined>();
  const acceptedAllConsents =
    acceptedAllConsentsFromUsercentrics ?? acceptedAllConsentsFromViewer;

  const refreshAcceptedAllConsents = useCallback((): void => {
    void areAllConsentsAccepted().then((allConsentsAccepted) => {
      if (allConsentsAccepted !== undefined) {
        setAcceptedAllConsents(allConsentsAccepted);
      }
    });
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      refreshAcceptedAllConsents();
    }

    const handleUsercentricsUiEvent = (event: Event): void => {
      const cmpEvent = event as CustomEvent<{ type?: string }>;

      if (
        !cmpEvent.detail?.type ||
        !CONSENT_CHANGE_EVENTS.has(cmpEvent.detail.type)
      ) {
        return;
      }

      refreshAcceptedAllConsents();
    };

    window.addEventListener(
      USERCENTRICS_UI_CMP_EVENT,
      handleUsercentricsUiEvent
    );
    window.addEventListener(
      USERCENTRICS_CONSENT_EVENT,
      refreshAcceptedAllConsents
    );

    return () => {
      window.removeEventListener(
        USERCENTRICS_UI_CMP_EVENT,
        handleUsercentricsUiEvent
      );
      window.removeEventListener(
        USERCENTRICS_CONSENT_EVENT,
        refreshAcceptedAllConsents
      );
    };
  }, [isLoggedIn, refreshAcceptedAllConsents]);

  const isLoadingLoggedInEligibilityData =
    isLoggedIn &&
    payOrConsentExperimentEnabled &&
    (loadingFeatures || loadingViewerData === true);
  const shouldUsePayOrConsentFlow =
    payOrConsentExperimentEnabled &&
    !activePayingMember &&
    !acceptedAllConsents;

  return {
    // Logged-in eligibility depends on features and viewer consent data, so we
    // render no consent UI until we know whether this user should see the wall.
    shouldHoldConsentUi: isLoadingLoggedInEligibilityData,
    shouldRenderPayOrConsentPlaceholder: shouldUsePayOrConsentFlow,
  };
}
